/**
 * A fileira de abas dos miolos.
 *
 * Papel: reproduz `.nav`/`.nav-tab` de Finanças (l. 46–52) e `.tabs`/`.tab-btn`
 * de Manutenções (l. 95–125): Outfit 11 px em caixa alta com `.08em`,
 * `padding:10px 14px`, sublinhado de 2 px, a ativa em latão e em 700, que o
 * navegador sintetiza porque o Outfit só é carregado em 300 e 400.
 *
 * Como usar: quem monta é o `QuadroEmbutido`, que guarda a aba ativa. O ícone é
 * o componente do `@tabler/icons-react` com o mesmo nome do `ti-*` da origem
 * (`ti-layout-dashboard` → `IconLayoutDashboard`), no tamanho da fonte da aba,
 * com o `.08em` do espaçamento entre letras à direita: a origem desenha o ícone
 * como um caractere, que recebe o espaçamento, e o SVG não recebe. A aba ativa
 * continua em latão sob o cursor, porque na origem `.active` vem depois de
 * `:hover` com a mesma especificidade.
 */
import { type Icon } from '@tabler/icons-react';

import { cn } from '@/shared/lib/utils';
import { type TemaEmbutido } from './tema-embutido';

/** Uma aba: a chave do painel, o texto e o ícone. */
export interface AbaDoApp {
  /** Liga a aba ao painel de mesmo nome em `paineis`. */
  chave: string;
  /** O texto, como a origem o escreve; a caixa alta vem do CSS. */
  rotulo: string;
  /** O ícone Tabler equivalente ao `ti-*` da origem. */
  Icone: Icon;
}

/** O recuo de cada origem. */
const RECUO_POR_TEMA: Readonly<Record<TemaEmbutido, string>> = {
  financeiro: 'pr-5 pl-8',
  manutencoes: 'px-5',
};

interface AbasDoAppProps {
  tema: TemaEmbutido;
  abas: readonly AbaDoApp[];
  /** A chave da aba ativa. */
  ativa: string;
  /** Chamado com a chave da aba clicada. */
  onTrocar: (chave: string) => void;
}

/**
 * As abas.
 *
 * @param tema - Qual origem as abas reproduzem.
 * @param abas - As abas, na ordem da origem.
 * @param ativa - A chave da aba ativa.
 * @param onTrocar - Recebe a chave da aba clicada.
 * @returns A fileira de abas.
 */
export function AbasDoApp({ tema, abas, ativa, onTrocar }: AbasDoAppProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex items-center gap-0.5 overflow-x-auto border-b border-emb-borda bg-superficie print:hidden',
        RECUO_POR_TEMA[tema],
      )}
    >
      {abas.map(({ chave, rotulo, Icone }) => {
        const selecionada = chave === ativa;
        return (
          <button
            key={chave}
            type="button"
            role="tab"
            id={`aba-${chave}`}
            aria-selected={selecionada}
            aria-controls={`painel-${chave}`}
            onClick={() => onTrocar(chave)}
            className={cn(
              'cursor-pointer border-b-2 border-transparent bg-transparent px-3.5 py-2.5 font-outfit text-[11px] font-normal tracking-[0.08em] whitespace-nowrap text-foreground uppercase transition-all duration-150 hover:text-emb-aba-hover',
              selecionada && 'border-brass font-bold text-brass hover:text-brass',
            )}
          >
            <Icone
              size={11}
              stroke={2}
              aria-hidden="true"
              className={cn(
                'inline-block align-[-1px]',
                tema === 'manutencoes' ? 'mr-[calc(4px+0.08em)]' : 'mr-[0.08em]',
              )}
            />{' '}
            {rotulo}
          </button>
        );
      })}
    </div>
  );
}
